import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, FileText, Sparkles, Trash2, Layers, Loader2, FolderOpen, Share2 } from "lucide-react";
import { listMaps, getMap, deleteMap, shareMap } from "../lib/api";
import { toast } from "sonner";

export default function MyMaps({ onClose, onOpen }) {
  const [maps, setMaps] = useState(null);
  const [opening, setOpening] = useState(null);

  const load = async () => {
    try { setMaps(await listMaps()); } catch { setMaps([]); }
  };
  useEffect(() => { load(); }, []);

  const open = async (id) => {
    setOpening(id);
    try {
      const data = await getMap(id);
      onOpen({ mapId: data.id, grids: data.grids, from_notes: data.from_notes, rootTopic: data.root_topic });
    } catch {
      toast.error("Could not open map.");
      setOpening(null);
    }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    await deleteMap(id);
    toast.success("Map deleted");
    load();
  };

  const share = async (e, id) => {
    e.stopPropagation();
    try {
      const shareId = await shareMap(id);
      const link = `${window.location.origin}/share/${shareId}`;
      try { await navigator.clipboard.writeText(link); toast.success("Share link copied to clipboard"); }
      catch { toast.success(`Share link: ${link}`); }
    } catch { toast.error("Could not create share link."); }
  };

  return (
    <div className="app-bg min-h-screen px-4 sm:px-6 py-8" data-testid="my-maps-screen">
      <div className="max-w-4xl mx-auto">
        <button data-testid="maps-back-btn" onClick={onClose}
          className="flex items-center gap-2 text-purple-300/80 hover:text-white mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to explorer
        </button>

        <h1 className="font-display text-3xl font-extrabold text-white glow-text">My Saved Maps</h1>
        <p className="text-purple-200/60 mt-1 mb-8">Reopen any explored fractal tree exactly where you left it.</p>

        {maps === null ? (
          <div className="flex justify-center py-20 text-purple-300"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : maps.length === 0 ? (
          <div className="text-center py-20 text-purple-300/60" data-testid="maps-empty">
            <FolderOpen className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No saved maps yet. Explore a subject and hit <span className="text-purple-200">Save Map</span>.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {maps.map((m, idx) => (
              <motion.button
                key={m.id}
                data-testid={`map-card-${idx}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.04 }}
                onClick={() => open(m.id)}
                className="group text-left rounded-2xl p-5 bg-surface-card/50 border border-purple-500/20 hover:border-purple-400/50 hover:-translate-y-1 transition-all relative overflow-hidden"
              >
                <div className="flex items-start justify-between">
                  <div className="p-2 rounded-xl bg-purple-500/12 border border-purple-500/25">
                    {opening === m.id ? <Loader2 className="w-5 h-5 text-purple-300 animate-spin" /> : <Layers className="w-5 h-5 text-purple-300" />}
                  </div>
                  <div className="flex items-center gap-2">
                    {m.from_notes && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/25 text-emerald-300 flex items-center gap-1">
                        <FileText className="w-3 h-3" /> notes
                      </span>
                    )}
                    <span onClick={(e) => share(e, m.id)} data-testid={`map-share-${idx}`}
                      className="p-1.5 rounded-lg text-purple-300/50 hover:text-purple-200 hover:bg-purple-500/10 transition-colors">
                      <Share2 className="w-4 h-4" />
                    </span>
                    <span onClick={(e) => remove(e, m.id)} data-testid={`map-delete-${idx}`}
                      className="p-1.5 rounded-lg text-purple-300/50 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </span>
                  </div>
                </div>
                <h3 className="font-display text-lg font-bold text-white mt-3">{m.title}</h3>
                <p className="text-xs text-purple-300/60 mt-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> {m.node_count} grid layer{m.node_count === 1 ? "" : "s"} explored
                </p>
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
